import { getPhoneValidationMessage, toE164Phone } from "code/phoneValidation";
import { z } from "zod";

/**
 * Reusable Zod field schema for an optional phone number.
 * - Accepts a local NZ number (e.g. "021 234 5678") or E.164 (e.g. "+6421234567")
 * - Validates with granular error messages (too short, too long, invalid, etc.)
 * - Transforms to E.164 on submit
 * - Empty / undefined input is treated as no phone provided
 */
export const phoneFieldSchema = z
	.string()
	.trim()
	.optional()
	.default("")
	.superRefine((phone, ctx) => {
		if (!phone) {
			return;
		}
		const message = getPhoneValidationMessage(phone);
		if (message) {
			ctx.addIssue({ code: "custom", message });
		}
	})
	.transform((phone): string => {
		if (!phone) {
			return phone;
		}
		return toE164Phone(phone);
	});

/**
 * Reusable Zod field schema for a required phone number.
 * Same as phoneFieldSchema but rejects empty values.
 */
export const phoneFieldRequiredSchema = z
	.string()
	.trim()
	.min(1, "Phone number is required")
	.superRefine((phone, ctx) => {
		const message = getPhoneValidationMessage(phone);
		if (message) {
			ctx.addIssue({ code: "custom", message });
		}
	})
	.transform((phone): string => toE164Phone(phone));
